import {useMemo} from 'react';

import type {AssignmentFilterInput} from 'gql/types';

import {useTeacherAssignmentsQuery} from '../queries/TeacherAssignments.queries.generated';

/** Cheap scope-wide count (ignores status + filters), to tell an empty scope from an empty tab. */
const useScopeHasAssignments = (
  classroomId: string | undefined,
  skip: boolean
): {hasAnyAssignments: boolean; isLoading: boolean} => {
  const variables = useMemo(() => {
    const classroomIds = classroomId ? [classroomId] : undefined;
    const filter: AssignmentFilterInput | undefined = classroomIds
      ? {assignedToClassrooms: {eq: true, input: {classroomIds}}}
      : undefined;
    return {classroomIds, filter, first: 1};
  }, [classroomId]);

  const {data, loading} = useTeacherAssignmentsQuery({
    fetchPolicy: 'cache-and-network',
    skip,
    variables
  });

  return {
    hasAnyAssignments: (data?.assignmentsAsEducator?.count ?? 0) > 0,
    isLoading: loading && !data
  };
};

interface Args {
  assignmentCount: number;
  hasFiltersOn: boolean;
  classroomId?: string;
}

interface AssignmentsScopeState {
  /** The scope-wide check is still resolving — show the list spinner, not a premature empty state. */
  isCheckingScope: boolean;
  /** The whole scope has no assignments at all — render the onboarding state. */
  isScopeEmpty: boolean;
}

/**
 * The design has a "no assignments yet" empty state for the whole scope — "scope" meaning all classes
 * (the cross-class view) or one class. To show it correctly we need to know whether the scope has zero
 * assignments across EVERY status, not just the active open/closed/... tab. The per-status list query
 * can't answer that, so this runs a separate scope-wide existence check (a cheap count that ignores
 * status + filters) and returns:
 *  - `isCheckingScope`: that check is still in flight — keep showing the list spinner.
 *  - `isScopeEmpty`: the scope truly has nothing — render the onboarding state.
 */
export const useAssignmentsScope = ({
  assignmentCount,
  hasFiltersOn,
  classroomId
}: Args): AssignmentsScopeState => {
  const {hasAnyAssignments, isLoading: isScopeLoading} = useScopeHasAssignments(
    classroomId,
    hasFiltersOn || assignmentCount > 0
  );

  // Only relevant when nothing is showing and no filter is narrowing the view.
  const isUndetermined = !hasFiltersOn && assignmentCount === 0;

  return {
    isCheckingScope: isUndetermined && isScopeLoading,
    isScopeEmpty: isUndetermined && !isScopeLoading && !hasAnyAssignments
  };
};
